/* eslint-disable react/no-array-index-key */
import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import Hr from '@components/atoms/Hr';
import COLORS from '@lib/constants/colors';
import ROUTES from '@lib/constants/routes';
import { customLink, linkResolver } from '@lib/data/prismic/helpers';
import { InsurancePageId } from '@lib/helpers/texts';
import Link from 'next/link';
import { RichText, RichTextBlock } from 'prismic-reactjs';
import { FC } from 'react';
import styled from 'styled-components';
import GridLinkList from './list/GridLinkList';
import RichTextWrapper from './RichTextWrapper';
import EmbedSlice from './slices/EmbedSlice';
import ImageWithCaption from './slices/ImageWithCaption/index';
import Quote from './slices/Quote';
import ReviewSlice from './slices/ReviewSlice';
import SliceFAQ from './slices/SliceFAQ';
import SliceNote from './slices/SliceNote';
import SliceProsCons from './slices/SliceProsCons';
import { TableOfContents } from './slices/TableOfContents';
import Text from './slices/Text';
import DefaultTable from './table/DefaultTable';

export const ButtonOnRichText = ({
  text,
  href = ROUTES.startForm(),
}: {
  text: string;
  href?: string;
}) => (
  <Link passHref href={href}>
    <a style={{ textDecoration: 'none' }}>
      <PrimaryButton pink boxShadow>
        {text}
      </PrimaryButton>
    </a>
  </Link>
);

export const SliceButtonLink = ({
  slice,
}: {
  slice: { primary: { text?: string; href?: string } };
}) =>
  slice.primary.href && slice.primary.text ? (
    <ButtonOnRichText href={slice.primary.href} text={slice.primary.text} />
  ) : null;

export const WrapperListGrid = styled.div`
  background: rgb(241, 244, 246);
  display: flex;
  position: relative;
  margin: 30px 0px;
  border-radius: 5px;

  &&& {
    ul {
      list-style: none;
      padding: 24px;
      margin: 0;
    }
  }

  a {
    color: ${COLORS.black};
  }
`;

export const SliceVehicleModels = ({
  slice,
}: {
  slice: {
    models: { model: string; modelSlug: string }[];
    brand: string;
    pageId: InsurancePageId;
  };
}) =>
  slice.models && slice.brand && slice.pageId ? (
    <WrapperListGrid>
      <GridLinkList
        prefetch={false}
        links={slice.models.map((model) => ({
          path: ROUTES.insurancePage.model(
            slice.pageId,
            slice.brand,
            model.modelSlug,
          ),
          label: `Seguro ${model.model}`,
        }))}
      />
    </WrapperListGrid>
  ) : null;

const TableSlice = ({
  slice,
}: {
  slice: {
    primary: { th1?: string; th2?: string };
    items: { col1: RichTextBlock[]; col2: RichTextBlock[] }[];
  };
}) => (
  <DefaultTable
    heads={[
      { text: slice.primary.th1 || '' },
      { text: slice.primary.th2 || '' },
    ]}
    rows={slice.items.map((item) => [
      {
        text: (
          <RichText
            render={item.col1}
            linkResolver={linkResolver}
            serializeHyperlink={customLink}
          />
        ),
      },
      {
        text: (
          <RichText
            render={item.col2}
            linkResolver={linkResolver}
            serializeHyperlink={customLink}
          />
        ),
      },
    ])}
  />
);

const SliceZone: FC<{ sliceZone: any[] }> = ({ sliceZone }) => (
  <RichTextWrapper>
    {sliceZone.map((slice, index) => {
      switch (slice.slice_type) {
        case 'tableofcontents':
          return (
            <TableOfContents
              slice={slice}
              sliceZone={sliceZone}
              key={`slice-${index}`}
            />
          );
        case 'embed':
          return <EmbedSlice slice={slice} key={`slice-${index}`} />;
        case 'review':
          return <ReviewSlice slice={slice} key={`slice-${index}`} />;
        case 'image_with_caption':
          return <ImageWithCaption slice={slice} key={`slice-${index}`} />;
        case 'table':
          return <TableSlice slice={slice} key={`slice-${index}`} />;
        case 'table__2_col':
          return <TableSlice slice={slice} key={`slice-${index}`} />;
        case 'table__2_col_':
          return <TableSlice slice={slice} key={`slice-${index}`} />;
        case 'quote':
          return <Quote slice={slice} key={`slice-${index}`} />;
        case 'text':
          return <Text slice={slice} key={`slice-${index}`} />;
        case 'faq':
          return <SliceFAQ slice={slice} key={`slice-${index}`} />;
        case 'divider':
          return <Hr key={`slice-${index}`} bgColor="#d2d3d4" />;
        case 'note':
          return <SliceNote slice={slice} key={`slice-${index}`} />;
        case 'pros_e_cons':
          return <SliceProsCons slice={slice} key={`slice-${index}`} />;
        case 'button':
          return <SliceButtonLink slice={slice} key={`slice-${index}`} />;
        case 'vehicle_models':
          return <SliceVehicleModels slice={slice} key={`slice-${index}`} />;
        default:
          return null;
      }
    })}
  </RichTextWrapper>
);

export default SliceZone;
